/**
 * Pure helpers for the post-call scoring flow on the Results page.
 *
 * WHY (feedback 7bc75409, Go-Forth, 2026-09-21): trainees finished a module
 * call and sat on "Analysis Still Processing ... refresh in 30 seconds" forever.
 * That screen was what the page showed when scoring had actually FAILED, the
 * status was never written back to sessionStorage (so a refresh re-entered the
 * same dead loop), and the only exits restarted the scenario. These helpers
 * give the page:
 *   - a bounded wait (per-request timeout + an overall deadline),
 *   - a clear failed state that can retry scoring for the SAME session,
 *   - a resume path on refresh (URL carries the session id; the saved attempt
 *     is re-read instead of the training being restarted).
 *
 * Kept free of React so it is unit-testable with `node --test`.
 */

export const RESULTS_STORAGE_KEY = 'lastTrainingResults';

/** A single /api/analysis/queue request may take this long before we give up on it. */
export const ANALYSIS_REQUEST_TIMEOUT_MS = 120_000;

/** Total time the page will spend waiting on scoring before showing the failed state. */
export const ANALYSIS_TOTAL_DEADLINE_MS = 180_000;

/** Transcript shorter than this cannot be scored (mirrors the server check). */
export const MIN_ANALYZABLE_TRANSCRIPT_CHARS = 50;

export const TIMEOUT_MESSAGE =
  'Scoring is taking longer than expected. Your call was saved — retry scoring without redoing the call.';

const TOO_SHORT_RE = /too short|no transcript|not.*captured/i;

/**
 * Legacy states written by older builds: 'slow' was the "still processing"
 * screen that actually meant scoring failed.
 */
export function normalizeAnalysisStatus(status) {
  if (status === 'slow') return 'failed';
  return status;
}

/**
 * Interpret one /api/analysis/queue (or /retry) HTTP response.
 * @param {{ ok: boolean, status: number, data: object }} res
 * @returns {{ outcome: 'completed'|'tooShort'|'failed', analysis?: object, retryable: boolean, message: string|null }}
 */
export function interpretQueueResponse({ ok, status, data }) {
  const body = data || {};
  if (ok && body.analysis) {
    return { outcome: 'completed', analysis: body.analysis, retryable: false, message: null };
  }
  if (body.errorType === 'too_short' || status === 422 || TOO_SHORT_RE.test(body.error || '')) {
    return { outcome: 'tooShort', retryable: false, message: body.error || null };
  }
  const retryable = body.retryable !== undefined ? Boolean(body.retryable) : true;
  return {
    outcome: 'failed',
    retryable,
    message: body.error || 'Scoring failed. Your call was saved — retry scoring.',
    errorType: body.errorType || null,
  };
}

/**
 * fetch wrapper that aborts after `ms`. Rejects with an Error whose
 * `code === 'TIMEOUT'` on timeout.
 */
export async function fetchWithTimeout(fetchFn, url, options = {}, ms = ANALYSIS_REQUEST_TIMEOUT_MS) {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => { timedOut = true; controller.abort(); }, ms);
  try {
    return await fetchFn(url, { ...options, signal: controller.signal });
  } catch (err) {
    if (timedOut) {
      const t = new Error(`Request timed out after ${ms}ms`);
      t.code = 'TIMEOUT';
      throw t;
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Run scoring with a bounded number of attempts and an overall deadline.
 * `requestOnce()` performs one HTTP call and resolves to {ok,status,data}
 * (it may reject, e.g. with code 'TIMEOUT' or a network error).
 *
 * Never resolves to anything but a final outcome, so the page can never be
 * left waiting indefinitely.
 */
export async function scoreWithDeadline(requestOnce, {
  maxAttempts = 2,
  deadlineMs = ANALYSIS_TOTAL_DEADLINE_MS,
  now = () => Date.now(),
} = {}) {
  const startedAt = now();
  let last = { outcome: 'failed', retryable: true, message: TIMEOUT_MESSAGE };
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      last = interpretQueueResponse(await requestOnce(attempt));
    } catch (err) {
      last = {
        outcome: 'failed',
        retryable: true,
        message: err?.code === 'TIMEOUT'
          ? TIMEOUT_MESSAGE
          : 'Could not reach the scoring service. Your call was saved — retry scoring.',
        errorType: err?.code === 'TIMEOUT' ? 'timeout' : 'network',
      };
      // A timed-out request already consumed a full request budget; don't
      // stack another one behind it.
      if (err?.code === 'TIMEOUT') break;
    }
    if (last.outcome !== 'failed' || !last.retryable) break;
    if (now() - startedAt >= deadlineMs) break;
  }
  return last;
}

/**
 * Read the stored in-flight results, optionally only if they belong to
 * `sessionId`. Returns null when absent, unreadable or for another session.
 */
export function readStoredResults(storage, sessionId = null) {
  try {
    const raw = storage?.getItem(RESULTS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (sessionId && parsed.sessionId !== sessionId) return null;
    return { ...parsed, analysisStatus: normalizeAnalysisStatus(parsed.analysisStatus) };
  } catch {
    return null;
  }
}

/** Persist results so a refresh resumes where the trainee was. Never throws. */
export function writeStoredResults(storage, results) {
  try {
    if (!results) return;
    storage?.setItem(RESULTS_STORAGE_KEY, JSON.stringify(results));
  } catch {
    /* storage full / blocked — the URL still carries the session id */
  }
}

/**
 * Status to show for a session loaded from the database (refresh with no
 * stored state, a new tab, or the history link).
 *   scored                -> 'completed'
 *   unscored, has a call  -> 'processing'  (resume: score it now)
 *   unscored, no call     -> 'unavailable'
 */
export function initialStatusForSession(session) {
  if (!session) return 'unavailable';
  if (session.overall_score !== null && session.overall_score !== undefined) return 'completed';
  const transcript = typeof session.transcript_raw === 'string' ? session.transcript_raw.trim() : '';
  if (transcript.length >= MIN_ANALYZABLE_TRANSCRIPT_CHARS) return 'processing';
  if (transcript.length > 0) return 'tooShort';
  return 'unavailable';
}

/** URL of the results page for one attempt — refresh-safe. */
export function resultsPathFor(sessionId) {
  return sessionId ? `/results/${encodeURIComponent(sessionId)}` : '/results';
}
